import { z } from "zod";

/**
 * Protocolo do terminal (seção 5.4): uma mensagem JSON por frame WebSocket.
 * O canal é individual (navegador ↔ sandbox do próprio dev) e nunca passa pelo Yjs.
 */
export const TerminalClientMessage = z.discriminatedUnion("t", [
  z.object({ t: z.literal("in"), d: z.string().max(64 * 1024) }),
  z.object({
    t: z.literal("resize"),
    cols: z.number().int().min(2).max(1000),
    rows: z.number().int().min(2).max(500),
  }),
  /** Pede confirmação de que o disco do sandbox reflete o estado compartilhado. */
  z.object({ t: z.literal("flush"), id: z.string().max(64) }),
]);
export type TerminalClientMessage = z.infer<typeof TerminalClientMessage>;

export type FsSyncState = "connecting" | "syncing" | "synced" | "error";

export type TerminalServerMessage =
  | { t: "out"; d: string }
  | { t: "exit"; code: number }
  | { t: "ready"; shell: string; pty: boolean }
  | { t: "fs"; state: FsSyncState; pending: number }
  | { t: "flushed"; id: string; ok: boolean }
  | { t: "error"; message: string };

/** Códigos de fechamento do WebSocket do terminal. */
export const TERMINAL_CLOSE = {
  unauthorized: 4401,
  forbidden: 4403,
  notFound: 4404,
  sandboxUnavailable: 4503,
  /** O sandbox já tem o máximo de terminais abertos (MAX_TERMINALS). */
  tooManyTerminals: 4429,
} as const;

export const COMMENT_MAX_CHARS = 4_000;
export const COMMENT_LIMITS = { threadsPerFile: 500, repliesPerThread: 100 } as const;

/**
 * Mensagens "stateless" do Hocuspocus usadas no modo sugestão.
 * Os documentos `suggestions:*` são somente leitura para os clientes:
 * toda mudança passa pelo servidor, que verifica o papel de quem pediu.
 */
export const SuggestionRequest = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("suggestion.create"),
    requestId: z.string().max(64),
    /** Y.RelativePosition codificada em base64 (início do trecho). */
    from: z.string().max(512),
    /** Y.RelativePosition codificada em base64 (fim do trecho). */
    to: z.string().max(512),
    text: z.string().max(20_000),
    note: z.string().max(2_000).optional(),
  }),
  z.object({ type: z.literal("suggestion.accept"), requestId: z.string().max(64), id: z.string().max(64) }),
  z.object({ type: z.literal("suggestion.reject"), requestId: z.string().max(64), id: z.string().max(64) }),
  z.object({ type: z.literal("suggestion.delete"), requestId: z.string().max(64), id: z.string().max(64) }),
  // Comentários na linha (revisão): mesmo canal, mesma checagem de papel no servidor.
  z.object({
    type: z.literal("comment.create"),
    requestId: z.string().max(64),
    /** Y.RelativePosition (base64) do início da linha comentada. */
    anchor: z.string().max(512),
    text: z.string().trim().min(1).max(COMMENT_MAX_CHARS),
  }),
  z.object({
    type: z.literal("comment.reply"),
    requestId: z.string().max(64),
    id: z.string().max(64),
    text: z.string().trim().min(1).max(COMMENT_MAX_CHARS),
  }),
  z.object({
    type: z.literal("comment.resolve"),
    requestId: z.string().max(64),
    id: z.string().max(64),
    resolved: z.boolean(),
  }),
  z.object({
    type: z.literal("comment.delete"),
    requestId: z.string().max(64),
    id: z.string().max(64),
    /** Só esta resposta; sem ela, a conversa inteira. */
    replyId: z.string().max(64).optional(),
  }),
]);
export type SuggestionRequest = z.infer<typeof SuggestionRequest>;

export interface SuggestionResponse {
  type: "suggestion.result";
  requestId: string;
  ok: boolean;
  error?: string;
}

export type SuggestionStatus = "open" | "accepted" | "rejected" | "stale";

export interface Suggestion {
  id: string;
  authorId: string;
  authorName: string;
  from: string;
  to: string;
  /** Texto original do trecho no momento da criação (para exibir o diff). */
  original: string;
  text: string;
  note?: string;
  status: SuggestionStatus;
  createdAt: number;
  resolvedBy?: string;
  resolvedByName?: string;
  resolvedAt?: number;
}

export interface CommentReply {
  id: string;
  authorId: string;
  authorName: string;
  text: string;
  createdAt: number;
}

/** Conversa presa a uma linha do arquivo (a posição acompanha as edições, como nas sugestões). */
export interface CommentThread {
  id: string;
  anchor: string;
  /** Linha quando a conversa começou (referência se o trecho for apagado). */
  line: number;
  authorId: string;
  authorName: string;
  text: string;
  createdAt: number;
  replies: CommentReply[];
  resolvedBy?: string;
  resolvedByName?: string;
  resolvedAt?: number;
}

/** Atividade publicada na presença (awareness). Nunca contém conteúdo do terminal. */
export const ACTIVITIES = ["editing", "running", "debugging", "stuck", "idle"] as const;
export type Activity = (typeof ACTIVITIES)[number];

export interface PresenceUser {
  id: string;
  name: string;
  handle: string;
  color: string;
  colorLight: string;
  avatarUrl?: string | null;
  role: string;
}

export interface PresenceState {
  user?: PresenceUser;
  activity?: Activity;
  currentFile?: string | null;
  needsHelp?: boolean;
}
